import React from "react";

// Humpty Dumpty Fall Scale (HDFS): 7 risk factors, each contributing a
// point value. A total score of 12 or above is the standard "High Risk"
// cutoff; below 12 is "Low Risk".
const HDFS_FIELDS = [
  "selectedAge",
  "selectedGender",
  "selectedDiagnosis",
  "selectedCognitive",
  "selectedEnviromental",
  "selectedSurgery",
  "selectedMedication",
];

const getHdfsScore = (hdfs) => {
  if (!hdfs) return null;
  return HDFS_FIELDS.reduce((sum, key) => {
    const val = Number(hdfs[key]);
    return sum + (Number.isFinite(val) ? val : 0);
  }, 0);
};

const getHdfsRisk = (score) => {
  if (score === null || score === undefined) return null;
  return score >= 12
    ? { label: "High Risk", color: "#e74c3c" }
    : { label: "Low Risk", color: "#27ae60" };
};

const formatDateTime = (value) => {
  if (!value) return null;
  return new Date(value).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Small "Created / Updated" timestamp line reused under every section.
const SectionTimestamps = ({ createdAt, updatedAt }) => {
  const created = formatDateTime(createdAt);
  const updated = formatDateTime(updatedAt);
  if (!created && !updated) return null;
  return (
    <div
      style={{
        fontSize: "11px",
        color: "#95a5a6",
        marginTop: "-10px",
        marginBottom: "12px",
      }}
    >
      {created && <>Created: {created}</>}
      {updated && updated !== created && <> &nbsp;·&nbsp; Updated: {updated}</>}
    </div>
  );
};

export const VisitListPrint = React.forwardRef(({ detailedVisits }, ref) => {
  return (
    <div
      ref={ref}
      style={{
        padding: "40px",
        fontFamily: "Arial, sans-serif",
        maxWidth: "310mm",
        margin: "0 auto",
        backgroundColor: "#fff",
      }}
    >
      <h1
        style={{
          textAlign: "center",
          color: "#2c3e50",
          borderBottom: "3px solid #3498db",
          paddingBottom: "15px",
          marginBottom: "30px",
        }}
      >
        Visit Summary Report
      </h1>

      {detailedVisits?.map((patient) => (
        <div key={patient.patientId} style={{ marginBottom: "40px" }}>
          <h2
            style={{
              color: "#2c3e50",
              backgroundColor: "#ecf0f1",
              padding: "12px 15px",
              borderLeft: "5px solid #3498db",
              marginBottom: "20px",
            }}
          >
            Patient:
            {` ${patient.patientDetails?.firstName} ${patient.patientDetails?.secondName} ${patient.patientDetails?.thirdName} ${patient.patientDetails?.fourthName}`}
          </h2>

          {patient.visits?.map((visit) => {
            const secretary = visit.secretarySection;
            const nurse = visit.nurseSection;
            const physician = visit.physicianSection;
            const hdfsScore = getHdfsScore(nurse?.HDFS);
            const hdfsRisk = getHdfsRisk(hdfsScore);

            return (
              <div
                key={visit.visitId}
                style={{
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  padding: "20px",
                  marginBottom: "20px",
                  backgroundColor: "#fafafa",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "15px",
                    paddingBottom: "10px",
                    borderBottom: "2px solid #e0e0e0",
                  }}
                >
                  <p style={{ margin: 0, fontSize: "16px" }}>
                    <strong>Visit Date:</strong>{" "}
                    {new Date(visit.visitDate).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                  <p style={{ margin: 0 }}>
                    <span
                      style={{
                        backgroundColor: visit.isLocked ? "#27ae60" : "#e74c3c",
                        color: "white",
                        padding: "4px 12px",
                        borderRadius: "4px",
                        fontSize: "14px",
                      }}
                    >
                      {visit.isLocked ? "Closed" : "Open"}
                    </span>
                  </p>
                </div>

                {/* ===================== 1. SECRETARY / SERVICES ===================== */}
                {secretary?.secretaryDetails && (
                  <div
                    style={{
                      backgroundColor: "#f4ecf7",
                      padding: "12px",
                      borderRadius: "6px",
                      marginBottom: "15px",
                    }}
                  >
                    <strong style={{ color: "#8e44ad" }}>Secretary:</strong>{" "}
                    {secretary.secretaryDetails.firstName}{" "}
                    {secretary.secretaryDetails.lastName}
                    {secretary.secretaryDetails.phone && (
                      <span style={{ color: "#7f8c8d" }}>
                        {" "}
                        ({secretary.secretaryDetails.phone})
                      </span>
                    )}
                  </div>
                )}
                {secretary && (
                  <SectionTimestamps
                    createdAt={secretary.createdAt}
                    updatedAt={secretary.updatedAt}
                  />
                )}

                {secretary?.visitServices?.length > 0 && (
                  <div style={{ marginBottom: "15px" }}>
                    <strong
                      style={{
                        color: "#2c3e50",
                        display: "block",
                        marginBottom: "8px",
                      }}
                    >
                      Services Provided:
                    </strong>
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {secretary.visitServices.map((service) => (
                        <li
                          key={service._id}
                          style={{
                            backgroundColor: "#fff",
                            padding: "10px 15px",
                            marginBottom: "8px",
                            borderLeft: "4px solid #27ae60",
                            borderRadius: "4px",
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span>{service.serviceLabel}</span>
                          <strong style={{ color: "#27ae60" }}>
                            {service.servicePrice} EGP
                          </strong>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {secretary?.paid !== undefined && (
                  <div
                    style={{
                      marginBottom: "15px",
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "30px",
                    }}
                  >
                    <div>
                      <span style={{ color: "#7f8c8d" }}>Paid:</span>{" "}
                      <strong style={{ color: "#27ae60" }}>
                        {secretary.paid} EGP
                      </strong>
                    </div>
                  </div>
                )}

                {/* ===================== 2. NURSE / FOLLOW-UP ===================== */}
                {nurse?.nurseDetails && (
                  <div
                    style={{
                      backgroundColor: "#fef9e7",
                      padding: "12px",
                      borderRadius: "6px",
                      marginBottom: "15px",
                    }}
                  >
                    <strong style={{ color: "#b7950b" }}>Nurse:</strong>{" "}
                    {nurse.nurseDetails.firstName} {nurse.nurseDetails.lastName}
                    {nurse.painScore && (
                      <span style={{ color: "#7f8c8d" }}>
                        {" "}
                        (Pain Score: {nurse.painScore})
                      </span>
                    )}
                  </div>
                )}
                {nurse && (
                  <SectionTimestamps
                    createdAt={nurse.createdAt}
                    updatedAt={nurse.updatedAt}
                  />
                )}

                {nurse?.HDFS && (
                  <div
                    style={{
                      backgroundColor: "#fff",
                      padding: "12px",
                      borderRadius: "4px",
                      border: "1px solid #e0e0e0",
                      marginBottom: "15px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <strong style={{ color: "#2c3e50" }}>
                      Humpty Dumpty Fall Score (HDFS):
                    </strong>
                    <span style={{ color: "#2c3e50" }}>{hdfsScore}</span>
                    {hdfsRisk && (
                      <span
                        style={{
                          backgroundColor: hdfsRisk.color,
                          color: "white",
                          padding: "3px 10px",
                          borderRadius: "4px",
                          fontSize: "13px",
                        }}
                      >
                        {hdfsRisk.label}
                      </span>
                    )}
                  </div>
                )}

                {nurse?.visitFollowups?.length > 0 && (
                  <div style={{ marginBottom: "15px" }}>
                    <strong
                      style={{
                        color: "#2c3e50",
                        display: "block",
                        marginBottom: "8px",
                      }}
                    >
                      Follow-Up Measurements:
                    </strong>
                    <div
                      style={{
                        backgroundColor: "#fff",
                        padding: "12px",
                        borderRadius: "4px",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      {nurse.visitFollowups.map((item) => (
                        <div
                          key={item._id}
                          style={{
                            display: "inline-block",
                            marginRight: "20px",
                            marginBottom: "8px",
                          }}
                        >
                          <span style={{ color: "#7f8c8d" }}>
                            {item.followupLabel}:
                          </span>{" "}
                          <strong style={{ color: "#2c3e50" }}>
                            {item.value}
                          </strong>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ===================== 3. PHYSICIAN ===================== */}
                {physician?.physicianDetails && (
                  <div
                    style={{
                      backgroundColor: "#e8f4f8",
                      padding: "12px",
                      borderRadius: "6px",
                      marginBottom: "15px",
                    }}
                  >
                    <strong style={{ color: "#2980b9" }}>Physician:</strong>{" "}
                    {physician.physicianDetails.firstName}{" "}
                    {physician.physicianDetails.lastName}
                    {physician.physicianDetails.phone && (
                      <span style={{ color: "#7f8c8d" }}>
                        {" "}
                        ({physician.physicianDetails.phone})
                      </span>
                    )}
                  </div>
                )}
                {physician && (
                  <SectionTimestamps
                    createdAt={physician.createdAt}
                    updatedAt={physician.updatedAt}
                  />
                )}

                {physician?.physicianNote && (
                  <div style={{ marginBottom: "15px" }}>
                    <strong
                      style={{
                        color: "#2c3e50",
                        display: "block",
                        marginBottom: "8px",
                      }}
                    >
                      Physician Note:
                    </strong>
                    <div
                      style={{
                        backgroundColor: "#fff",
                        padding: "12px",
                        borderLeft: "4px solid #3498db",
                        borderRadius: "4px",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: physician.physicianNote,
                      }}
                    />
                  </div>
                )}

                {physician?.visitCheckLists?.length > 0 && (
                  <div style={{ marginBottom: "15px" }}>
                    <strong
                      style={{
                        color: "#2c3e50",
                        display: "block",
                        marginBottom: "8px",
                      }}
                    >
                      Checklists:
                    </strong>
                    {physician.visitCheckLists.map((checklist) => (
                      <div
                        key={checklist._id}
                        style={{
                          marginBottom: "12px",
                          backgroundColor: "#fff",
                          padding: "10px",
                          borderRadius: "4px",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <strong style={{ color: "#34495e" }}>
                          {checklist.title}
                        </strong>
                        {checklist.subtitles?.length > 0 && (
                          <ul
                            style={{
                              marginTop: "8px",
                              marginBottom: 0,
                              paddingLeft: "25px",
                            }}
                          >
                            {checklist.subtitles.map((sub) => (
                              <li
                                key={sub.subtitleId}
                                style={{ marginBottom: "5px", color: "#555" }}
                              >
                                {sub.subtitle && (
                                  <strong>{sub.subtitle}</strong>
                                )}
                                {sub.label && (
                                  <span style={{ color: "#7f8c8d" }}>
                                    {" — "}
                                    {sub.label}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {physician?.rosheta?.length > 0 && (
                  <div style={{ marginBottom: "15px" }}>
                    <strong
                      style={{
                        color: "#2c3e50",
                        display: "block",
                        marginBottom: "8px",
                      }}
                    >
                      {`Prescription(s):`}
                    </strong>
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {physician.rosheta.map((r) => (
                        <li
                          key={r._id}
                          style={{
                            backgroundColor: "#fff",
                            padding: "10px 15px",
                            marginBottom: "8px",
                            borderLeft: "4px solid #e67e22",
                            borderRadius: "4px",
                          }}
                        >
                          <strong>{r.tradeLabel}</strong>
                          <div
                            style={{
                              color: "#7f8c8d",
                              fontSize: "14px",
                              marginTop: "4px",
                            }}
                          >
                            {r.direction}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <style>{`
        @media print {
          body {
            margin: 0;
          }

          @page {
            margin: 15mm 10mm 20mm 10mm;

            @bottom-center {
              content: "Page " counter(page) " of " counter(pages);
              font-family: Arial, sans-serif;
              font-size: 11px;
              color: #7f8c8d;
              border-top: 1px solid #e0e0e0;
              padding-top: 4px;
            }
          }
        }
      `}</style>
    </div>
  );
});
